import type {
  BitcoinTransactionInput,
  DecodedTransaction,
  DecodedField,
} from '@txviz/core';
import {
  formatValue,
  bytesToHex,
  sanitizeDisplayString,
} from '@txviz/core';
import { serializeBitcoinTransaction } from './serializer.js';
import { detectScriptType, hasWitness, deriveAddress } from './types.js';

/**
 * Decode a Bitcoin transaction into human-readable fields.
 */
export function decodeBitcoinTransaction(tx: BitcoinTransactionInput): DecodedTransaction {
  const fields: DecodedField[] = [];
  const serialized = serializeBitcoinTransaction(tx);
  const rawHex = bytesToHex(serialized);
  const isSegwit = hasWitness(tx);

  // Transaction format
  fields.push({
    label: 'Format',
    value: isSegwit ? 'SegWit' : 'Legacy',
    rawValue: isSegwit ? 'segwit' : 'legacy',
    type: 'text',
  });

  // Version
  fields.push({
    label: 'Version',
    value: tx.version.toString(),
    rawValue: tx.version.toString(),
    type: 'number',
  });

  // Inputs
  fields.push({
    label: 'Inputs',
    value: `${tx.inputs.length} input(s)`,
    rawValue: tx.inputs.length.toString(),
    type: 'number',
  });

  for (let i = 0; i < tx.inputs.length; i++) {
    const input = tx.inputs[i]!;
    fields.push({
      label: `Input ${i}`,
      value: `${input.txid}:${input.vout}`,
      rawValue: `${input.txid}:${input.vout}`,
      type: 'data',
    });

    if (input.value !== undefined) {
      fields.push({
        label: `Input ${i} Value`,
        value: formatValue(input.value, 'bitcoin'),
        rawValue: input.value.toString(),
        type: 'value',
      });
    }
  }

  // Outputs
  fields.push({
    label: 'Outputs',
    value: `${tx.outputs.length} output(s)`,
    rawValue: tx.outputs.length.toString(),
    type: 'number',
  });

  let totalOutputValue = 0n;
  for (let i = 0; i < tx.outputs.length; i++) {
    const output = tx.outputs[i]!;
    const scriptType = detectScriptType(output.scriptPubKey);
    // Derive address from scriptPubKey if not explicitly provided
    const address = output.address ?? deriveAddress(output.scriptPubKey);

    fields.push({
      label: `Output ${i}`,
      value: `${formatValue(output.value, 'bitcoin')} → ${address ?? scriptType}`,
      rawValue: `${output.value}:${output.scriptPubKey}`,
      type: 'value',
    });

    if (address) {
      fields.push({
        label: `Output ${i} Address`,
        value: address,
        rawValue: address,
        type: 'address',
      });
    }

    fields.push({
      label: `Output ${i} Type`,
      value: scriptType.toUpperCase(),
      rawValue: scriptType,
      type: 'text',
    });

    totalOutputValue += output.value;
  }

  // Fee calculation (if all input values are known)
  const allInputValuesKnown = tx.inputs.every((input) => input.value !== undefined);
  if (allInputValuesKnown) {
    let totalInputValue = 0n;
    for (const input of tx.inputs) {
      totalInputValue += input.value!;
    }
    const fee = totalInputValue - totalOutputValue;
    fields.push({
      label: 'Fee',
      value: formatValue(fee, 'bitcoin'),
      rawValue: fee.toString(),
      type: 'value',
      warning: fee < 0n ? 'Negative fee — transaction is invalid' : undefined,
    });
  }

  // Locktime
  if (tx.locktime > 0) {
    const locktimeLabel =
      tx.locktime < 500_000_000
        ? `Block ${tx.locktime}`
        : `Timestamp ${new Date(tx.locktime * 1000).toISOString()}`;

    fields.push({
      label: 'Locktime',
      value: locktimeLabel,
      rawValue: tx.locktime.toString(),
      type: 'number',
    });
  }

  // Build summary
  const summary = buildSummary(tx, totalOutputValue);

  return {
    chain: 'bitcoin',
    type: isSegwit ? 'SegWit Transaction' : 'Legacy Transaction',
    summary,
    fields,
    rawHex,
  };
}

function buildSummary(
  tx: BitcoinTransactionInput,
  totalOutputValue: bigint,
): string {
  const parts: string[] = [];

  parts.push(`${tx.inputs.length} input(s) → ${tx.outputs.length} output(s)`);
  parts.push(`Total: ${formatValue(totalOutputValue, 'bitcoin')}`);

  if (hasWitness(tx)) {
    parts.push('(SegWit)');
  }

  return sanitizeDisplayString(parts.join(', '));
}
